import { DatePipe } from '@angular/common';
import { Component, OnDestroy, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { ImageUrlPipe, InsightMarkdownPipe, VideoPlayersModule } from '@frontend/shared';
import { TranslocoModule } from '@jsverse/transloco';
import { AlertModule } from 'ngx-bootstrap/alert';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { reloadOnLocaleChange } from '../../language';
import { LoaderGlobeAnimationDetailComponent, LoadingObject, LoadingService } from '../../loaders';
import { PageTitleService } from '../../navigation/title/title.service';
import { WindowService } from '../../utilities';
import { NewsFeedDefinition, NewsItem } from '../news.model';
import { NewsService } from '../news.service';

const MORE_NEWS_SHOWN = 5;

/**
 * One news item's page, `/news/{slug}`.
 *
 * ## Translation, as CultureConnector's article reader does it
 *
 * The reader is never asked whether they want their own language. When the
 * item arrives in English because the requested language has not been
 * translated (`absent`) or its translation failed, this page asks for it,
 * shows the English with a spinner while the answer says it is worth waiting,
 * and swaps the text in once, when the translation settles. A `partial` or
 * `refused` translation is not asked for again: nothing further will happen
 * until an editor changes the English, which re-queues it by itself.
 *
 * Built from the tip page's layout: the item in `col-lg-8`, and a sticky
 * sidebar card in `col-lg-4` with more news from the same feed.
 */
@Component({
  selector: 'multisite-news-item',
  templateUrl: './news-item.component.html',
  imports: [
    DatePipe,
    RouterModule,
    TranslocoModule,
    AlertModule,
    VideoPlayersModule,
    LoaderGlobeAnimationDetailComponent,
    ImageUrlPipe,
    InsightMarkdownPipe,
  ],
})
export class NewsItemComponent implements OnDestroy {
  private route = inject(ActivatedRoute);
  private newsService = inject(NewsService);
  private loadingService = inject(LoadingService);
  private pageTitleService = inject(PageTitleService);
  private windowService = inject(WindowService);

  readonly feed: NewsFeedDefinition = this.route.snapshot.data['newsFeed'];

  newsItem = signal<NewsItem | null>(null);
  moreNews = signal<NewsItem[]>([]);
  notFound = signal(false);
  failed = signal(false);
  translating = signal(false);

  loadingObject = signal<LoadingObject>({});
  isAnythingLoading = computed(() => this.loadingService.isAnythingLoading(this.loadingObject()));

  /** The item's image: a hero if it has one, else its first. */
  image = computed(() => {
    const images = this.newsItem()?.images ?? [];

    return images.find((image) => image.role === 'hero') ?? images[0] ?? null;
  });

  private subscriptions: Subscription[] = [];
  private translation?: Subscription;
  private slug = '';

  constructor() {
    this.subscriptions.push(
      this.route.paramMap.subscribe((params) => {
        this.slug = params.get('slug') ?? '';
        this.windowService.goToTop();
        this.load();
      }),
    );

    reloadOnLocaleChange(() => this.load());
  }

  setLoadingStatus(key: string, value: boolean): void {
    this.loadingObject.update((current) => this.loadingService.setLoadingStatus(key, value, current));
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.translation?.unsubscribe();
  }

  private load(): void {
    this.translation?.unsubscribe();
    this.translating.set(false);
    this.notFound.set(false);
    this.failed.set(false);

    const slug = this.slug;
    this.setLoadingStatus('news:item', true);
    this.subscriptions.push(
      this.newsService
        .getNewsItem(slug)
        .pipe(finalize(() => this.setLoadingStatus('news:item', false)))
        .subscribe({
          next: (item) => {
            this.show(item);
            this.translateIfNeeded(item);
          },
          error: (error) => {
            this.newsItem.set(null);
            error?.status === 404 ? this.notFound.set(true) : this.failed.set(true);
          },
        }),
    );

    this.loadMoreNews(slug);
  }

  private show(item: NewsItem): void {
    this.newsItem.set(item);
    this.pageTitleService.setTitle(item.title);
  }

  private translateIfNeeded(item: NewsItem): void {
    if (NewsService.needsTranslation(item)) {
      this.translating.set(true);
      this.translation = this.newsService.requestTranslation(item.slug, item.requestedLocale).subscribe({
        next: (answer) => this.follow(answer),
        error: () => this.translating.set(false),
      });
    } else {
      this.follow(item);
    }
  }

  /** Show what arrived, and keep watching while it is worth waiting. */
  private follow(item: NewsItem): void {
    this.show(item);

    if (!item.worthPolling) {
      this.translating.set(false);
      return;
    }

    this.translating.set(true);
    this.translation = this.newsService
      .watchTranslation(item.slug)
      .pipe(finalize(() => this.translating.set(false)))
      .subscribe((settled) => this.show(settled));
  }

  private loadMoreNews(slug: string): void {
    this.subscriptions.push(
      this.newsService.getNews({ filter: this.feed?.defaultFilter ?? {}, pageSize: MORE_NEWS_SHOWN + 1 }).subscribe({
        next: (page) => this.moreNews.set(page.items.filter((item) => item.slug !== slug).slice(0, MORE_NEWS_SHOWN)),
        error: () => this.moreNews.set([]),
      }),
    );
  }
}
